/**
 * DrawerStackProvider — provider React du contexte de pile de drawers (S5b).
 *
 * À placer haut dans l'arbre — on le branche au niveau `AppShell` pour qu'il
 * couvre toutes les routes authentifiées d'un coup. Une seule pile globale
 * suffit (un drawer dans une page n'empêche pas un autre drawer dans la
 * sidebar — la pile gère naturellement l'ordre LIFO).
 */
import { useCallback, useRef, useState, type ReactNode } from 'react'

import { DrawerStackContext, type DrawerStackContextValue } from './drawer-stack-context'

interface DrawerStackProviderProps {
  children: ReactNode
}

export function DrawerStackProvider({ children }: DrawerStackProviderProps) {
  const [depth, setDepth] = useState(0)
  // Ref pour incrémenter de façon synchronisée — sinon deux Drawer qui
  // s'enregistrent dans le même render frame se voient le même `depth`.
  const counter = useRef(0)

  const register = useCallback(() => {
    counter.current += 1
    setDepth((d) => d + 1)
    return counter.current
  }, [])

  const unregister = useCallback((_index: number) => {
    setDepth((d) => Math.max(0, d - 1))
  }, [])

  const value: DrawerStackContextValue = { register, unregister, depth }
  return <DrawerStackContext.Provider value={value}>{children}</DrawerStackContext.Provider>
}
